import { z } from 'zod'
import { CompanyFactSchemaVersion } from './core'

export enum OnsiteRequired {
  YES = 'YES',
  NO = 'NO',
  PEAK_ONLY = 'PEAK_ONLY',
}

export enum OnsiteCostAllocation {
  TRABA = 'TRABA',
  BUSINESS = 'BUSINESS',
  SPLIT = 'SPLIT',
}

export enum OnsiteType {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  WORKING_SHIFT_LEAD = 'WORKING_SHIFT_LEAD',
}

export enum OnsiteResponsibility {
  WORKER_SUPPORT = 'WORKER_SUPPORT',
  CLOCK_IN_OUT = 'CLOCK_IN_OUT',
  REPORTING = 'REPORTING',
  WORKER_PERFORMANCE = 'WORKER_PERFORMANCE',
  DRUG_TESTS = 'DRUG_TESTS',
  TOURS_ORIENTATIONS = 'TOURS_ORIENTATIONS',
  WORKER_ONBOARDING = 'WORKER_ONBOARDING',
  WORKER_CHECK_IN = 'WORKER_CHECK_IN',
  BUSINESS_COMMUNICATION = 'BUSINESS_COMMUNICATION',
  INCIDENT_MANAGEMENT = 'INCIDENT_MANAGEMENT',
  WORKING_THE_SHIFT = 'WORKING_THE_SHIFT',
}

export interface OnsitePaymentTraba {
  costAllocation: OnsiteCostAllocation.TRABA
}
export interface OnsitePaymentBusiness {
  costAllocation: OnsiteCostAllocation.BUSINESS
}
export interface OnsitePaymentSplit {
  costAllocation: OnsiteCostAllocation.SPLIT
  splitPercent: number
}
export type OnsitePayment =
  | OnsitePaymentTraba
  | OnsitePaymentBusiness
  | OnsitePaymentSplit

export type OnsiteStrategyV1Value =
  | {
      required: OnsiteRequired.NO
    }
  | {
      required: OnsiteRequired.YES | OnsiteRequired.PEAK_ONLY
      payment: OnsitePayment
      onsiteType: OnsiteType
      responsibilities: OnsiteResponsibility[]
    }

export interface OnsiteStrategyV1FactValue {
  version: 'ONSITE_STRATEGY_V1'
  value: OnsiteStrategyV1Value
  notes?: string
  justification?: string
}

const NotesSchema = z.string().optional()
const JustificationSchema = z.string().optional()

export const OnsitePaymentSchema = z.discriminatedUnion('costAllocation', [
  z.object({ costAllocation: z.literal(OnsiteCostAllocation.TRABA) }),
  z.object({ costAllocation: z.literal(OnsiteCostAllocation.BUSINESS) }),
  z.object({
    costAllocation: z.literal(OnsiteCostAllocation.SPLIT),
    splitPercent: z.number().min(0).max(100),
  }),
])

export const OnsiteStrategyV1ValueSchema = z.discriminatedUnion('required', [
  z.object({
    required: z.literal(OnsiteRequired.NO),
  }),
  z.object({
    required: z.literal(OnsiteRequired.YES),
    payment: OnsitePaymentSchema,
    onsiteType: z.nativeEnum(OnsiteType),
    responsibilities: z.array(z.nativeEnum(OnsiteResponsibility)),
  }),
  z.object({
    required: z.literal(OnsiteRequired.PEAK_ONLY),
    payment: OnsitePaymentSchema,
    onsiteType: z.nativeEnum(OnsiteType),
    responsibilities: z.array(z.nativeEnum(OnsiteResponsibility)),
  }),
])

export const OnsiteStrategyV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.ONSITE_STRATEGY_V1),
  value: OnsiteStrategyV1ValueSchema,
  notes: NotesSchema,
  justification: JustificationSchema,
})
